import {ArtistContextDialog} from '@app/web-player/artists/artist-context-dialog';
import {ArtistLinks} from '@app/web-player/artists/artist-links';
import {LikeIconButton} from '@app/web-player/library/like-icon-button';
import {DownloadTrackButton} from '@app/web-player/player-controls/download-track-button';
import {LyricsButton} from '@app/web-player/player-controls/lyrics-button';
import {PlaybackControls} from '@app/web-player/player-controls/playback-controls';
import {useCuedTrack} from '@app/web-player/player-controls/use-cued-track';
import {
  playerOverlayState,
  usePlayerOverlayStore,
} from '@app/web-player/state/player-overlay-store';
import {TrackContextDialog} from '@app/web-player/tracks/context-dialog/track-context-dialog';
import {TrackImage} from '@app/web-player/tracks/track-image/track-image';
import {getTrackLink, TrackLink} from '@app/web-player/tracks/track-link';
import {usePlayerStore} from '@common/player/hooks/use-player-store';
import {FullscreenButton} from '@common/player/ui/controls/fullscreen-button';
import {VolumeControls} from '@common/player/ui/controls/volume-controls';
import {DashboardLayoutContext} from '@common/ui/dashboard-layout/dashboard-layout-context';
import {IconButton} from '@ui/buttons/icon-button';
import {Trans} from '@ui/i18n/trans';
import {KeyboardArrowDownIcon} from '@ui/icons/material/KeyboardArrowDown';
import {KeyboardArrowUpIcon} from '@ui/icons/material/KeyboardArrowUp';
import {MediaQueueListIcon} from '@ui/icons/media/media-queue-list';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {useSettings} from '@ui/settings/use-settings';
import {Tooltip} from '@ui/tooltip/tooltip';
import clsx from 'clsx';
import {ReactNode, useContext} from 'react';
import {createPortal} from 'react-dom';
import {Link} from 'react-router';
import {DESKTOP_PLAYER_FOOTER_HEIGHT_CLASS} from '@app/web-player/player/mobile-video-frame';

export function DesktopPlayerControls() {
  const mediaIsCued = usePlayerStore(s => s.cuedMedia != null);
  const isFullscreen = usePlayerStore(s => s.isFullscreen);
  const isMaximized = usePlayerOverlayStore(s => s.isMaximized);

  if (!mediaIsCued) return null;

  // Native fullscreen overlay has its own transport + seekbar.
  if (isFullscreen && isMaximized) {
    return null;
  }

  const bar = (
    <div
      className={clsx(
        'dashboard-grid-footer player-section flex items-center justify-between px-16',
        DESKTOP_PLAYER_FOOTER_HEIGHT_CLASS,
        isMaximized
          ? 'fixed inset-x-0 bottom-0 z-[55] m-0 border-x-0 border-t border-divider-lighter border-b-0 bg-elevated shadow-[0_-4px_24px_rgba(0,0,0,0.12)] dark:border-divider dark:shadow-[0_-4px_24px_rgba(0,0,0,0.45)]'
          : 'z-[50] m-8',
      )}
    >
      <QueuedTrack />
      <PlaybackControls className="w-2/5 max-w-[722px]" />
      <SecondaryControls />
    </div>
  );

  // Expanded overlay (z-42) sits above dashboard `isolate` stacking — portal when maximized.
  if (isMaximized && typeof document !== 'undefined') {
    return createPortal(bar, document.body);
  }

  return bar;
}

function QueuedTrack() {
  const track = useCuedTrack();
  let content: ReactNode;

  if (track) {
    content = (
      <div className="flex items-center gap-14">
        <DialogTrigger type="popover" triggerOnContextMenu placement="top">
          <Link to={getTrackLink(track)} className="flex-shrink-0">
            <TrackImage
              className="h-56 w-56 rounded object-cover"
              track={track}
            />
          </Link>
          <TrackContextDialog tracks={[track]} />
        </DialogTrigger>
        <div className="min-w-0 overflow-hidden overflow-ellipsis">
          <DialogTrigger type="popover" triggerOnContextMenu placement="top">
            <TrackLink
              track={track}
              className="min-w-0 max-w-full whitespace-nowrap text-sm"
            />
            <TrackContextDialog tracks={[track]} />
          </DialogTrigger>
          {track.artists?.length ? (
            <DialogTrigger type="popover" triggerOnContextMenu placement="top">
              <div className="text-xs text-muted">
                <ArtistLinks
                  artists={track.artists}
                  className="whitespace-nowrap"
                />
              </div>
              <ArtistContextDialog artist={track.artists[0]} />
            </DialogTrigger>
          ) : null}
        </div>
        <LikeIconButton likeable={track} />
      </div>
    );
  } else {
    content = null;
  }

  return <div className="w-[30%] min-w-180">{content}</div>;
}

function SecondaryControls() {
  const {rightSidenavStatus, setRightSidenavStatus} = useContext(
    DashboardLayoutContext,
  );
  const haveVideo = usePlayerStore(
    s => s.providerApi != null && s.providerName !== 'htmlAudio',
  );
  const {player} = useSettings();

  return (
    <div className="flex w-[30%] min-w-180 items-center justify-end">
      <LyricsButton />
      <DownloadTrackButton />
      <Tooltip label={<Trans message="Queue" />}>
        <IconButton
          className="flex-shrink-0"
          onClick={() => {
            setRightSidenavStatus(
              rightSidenavStatus === 'closed' ? 'open' : 'closed',
            );
          }}
        >
          <MediaQueueListIcon />
        </IconButton>
      </Tooltip>
      {haveVideo && !player?.hide_video_button && (
        <FullscreenButton className="flex-shrink-0" />
      )}
      <VolumeControls trackColor="neutral" />
      <OverlayButton />
    </div>
  );
}

function OverlayButton() {
  const isActive = usePlayerOverlayStore(s => s.isMaximized);
  const playerReady = usePlayerStore(s => s.providerReady);
  const {player} = useSettings();

  if (player?.hide_video_button) {
    return null;
  }

  return (
    <Tooltip label={<Trans message={isActive ? 'Minimize' : 'Expand'} />}>
      <IconButton
        className="ml-26 flex-shrink-0"
        color="chip"
        variant="flat"
        size="xs"
        iconSize="sm"
        disabled={!playerReady}
        onClick={() => {
          playerOverlayState.toggle();
        }}
      >
        {isActive ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
      </IconButton>
    </Tooltip>
  );
}
